/**
 * Cache Manager Service
 * Gestisce la versione dell'app e l'invalidazione della cache
 */

import packageJson from '../package.json';
import { storage } from './storage';
import { logger } from '@deskbay/shared';

const APP_VERSION_KEY = 'deskbay_app_version';
const ONBOARDING_PROGRESS_KEY = 'deskbay_onboarding_progress';

export interface AppVersion {
    version: string;
    lastUpdate: number;
}

/**
 * Versione corrente dell'app (sincronizzata automaticamente con package.json)
 */
export const CURRENT_APP_VERSION = packageJson.version;

/**
 * Controlla se la cache è valida confrontando con la versione corrente
 */
export async function checkCacheValidity(): Promise<boolean> {
    try {
        const storedVersion = await storage.get(APP_VERSION_KEY);

        if (!storedVersion) {
            // Prima installazione
            return false;
        }

        const parsed: AppVersion = JSON.parse(storedVersion);
        return parsed.version === CURRENT_APP_VERSION;
    } catch (error) {
        logger.performance.error('Error checking cache validity', error, 'CacheManager.checkCacheValidity');
        return false;
    }
}

/**
 * Aggiorna la versione dell'app in storage
 */
export async function updateAppVersion(): Promise<void> {
    const versionData: AppVersion = {
        version: CURRENT_APP_VERSION,
        lastUpdate: Date.now()
    };

    await storage.set(APP_VERSION_KEY, JSON.stringify(versionData));
}

/**
 * Invalida la cache e pulisce i dati di onboarding obsoleti
 * Viene chiamata quando viene rilevata una nuova versione
 */
export async function invalidateCache(): Promise<void> {
    logger.performance.info(`Nuova versione rilevata: ${CURRENT_APP_VERSION}`, 'cacheManager');

    try {
        // Rimuovi progressi onboarding (potenzialmente incompatibili con nuova versione)
        await storage.remove(ONBOARDING_PROGRESS_KEY);

        // Pulisci altre cache se necessario
        // Ad esempio: rimuovi dati temporanei, stati parziali, etc.
        const keysToRemove = [
            'deskbay_temp_restaurant',
            'deskbay_draft_menu',
            'deskbay_upload_state'
        ];

        for (const key of keysToRemove) {
            const value = await storage.get(key);
            if (value) {
                await storage.remove(key);
            }
        }

        // Pulisci Cache API se presente
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            logger.performance.debug('Cache API pulita', 'cacheManager');
        }

        // Aggiorna la versione
        updateAppVersion();

        logger.performance.info('Cache invalidata con successo', 'cacheManager');
    } catch (error) {
        logger.performance.error('Errore durante invalidazione cache', error, 'cacheManager');
    }
}

/**
 * Inizializza il sistema di cache management
 * Da chiamare all'avvio dell'app
 */
export async function initializeCacheManager(): Promise<void> {
    const isValid = await checkCacheValidity();

    if (!isValid) {
        await invalidateCache();
    } else {
        logger.performance.debug(`Cache valida - Versione: ${CURRENT_APP_VERSION}`, 'cacheManager');
    }
}

/**
 * Forza il refresh della cache (utile per debug o casi eccezionali)
 */
export function forceCacheRefresh(): void {
    logger.performance.warn('Forzatura refresh cache', 'cacheManager');
    invalidateCache().then(() => {
        window.location.reload();
    });
}
